/* eslint-disable max-lines */
import {
  calculateRecurringEffectiveEndDate,
  calculateRecurringEndDateByNumOfOccurrences,
  getRecurringStartDateHelperText,
  RECURRING_NUM_OF_OCCURRENCES_REGEX,
  RECURRING_SINGLE_PAYMENT,
} from '@melio/ap-domain';
import { AccountingPlatformCategorySelectWidget, VendorSelectWidget } from '@melio/ap-widgets';
import { Form, OnSubmissionStateChange, useMelioForm } from '@melio/penny';
import { useAnalytics, useTrackFirstKeyPress } from '@melio/platform-analytics';
import {
  AccountingPlatform,
  AccountingPlatformSlug,
  BillSubscriptionEndPolicyEnum,
  BillSubscriptionIntervalTypeEnum,
  useVendor,
} from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useDateUtils } from '@melio/platform-utils';
import { isEmpty, keys } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { RecurringInfoSection } from '../../add-bill/AddBillV2Form/components/RecurringInfoSection';
import { useAddInstantBillV2FormSchema } from './schema';
import {
  AddInstantBillV2FormActivityFields,
  recurringEndDateFields,
  recurringNumOfOccurrencesFields,
  singlePaymentFields,
} from './types';

export type AddInstantBillV2FormActivityProps = {
  defaultValues?: Partial<AddInstantBillV2FormActivityFields>;
  isSaving?: boolean;
  isLoading?: boolean;
  activeAccountingPlatform?: AccountingPlatform;
  isRecurringPaymentsEnabled?: boolean;
  isInternationalSupportedForRecurringFlow?: boolean;
  onUpdateFrequency?: (isRecurring: boolean) => void;
  onSubmit: (data: AddInstantBillV2FormActivityFields) => void;
  onSubmissionStateChange: OnSubmissionStateChange<AddInstantBillV2FormActivityFields>;
  onSelectedVendorChange?: (vendorId: string) => void;
};

export const AddInstantBillV2FormActivity = ({
  defaultValues,
  isSaving,
  isLoading,
  activeAccountingPlatform,
  isRecurringPaymentsEnabled = false,
  isInternationalSupportedForRecurringFlow = false,
  onUpdateFrequency,
  onSubmit,
  onSubmissionStateChange,
  onSelectedVendorChange,
  ...props
}: AddInstantBillV2FormActivityProps) => {
  const [fieldsForValidation, setFieldsForValidation] = useState(singlePaymentFields);
  const [isRecurringSupported, setIsRecurringSupported] = useState(false);
  const { formatMessage, formatDate } = useMelioIntl();
  const { createDate } = useDateUtils();
  const { registerField, formProps, setValue, watch, formState } = useMelioForm<AddInstantBillV2FormActivityFields>({
    onSubmit,
    schema: useAddInstantBillV2FormSchema(fieldsForValidation),
    defaultValues: {
      ...defaultValues,
      paymentFrequency: RECURRING_SINGLE_PAYMENT,
      endPolicy: BillSubscriptionEndPolicyEnum.EndDate,
    },
    isSaving,
    onSubmissionStateChange,
    isLoading,
  });

  const isQuickBooksOnline = activeAccountingPlatform?.accountingSlug === AccountingPlatformSlug.QuickBooksOnline;
  const accountingPlatformCategoryLabel = isQuickBooksOnline
    ? formatMessage('activities.addInstantBillV2Form.category.label.quickbooks')
    : formatMessage('activities.addInstantBillV2Form.category.label.general');

  const watchVendorId = watch('vendorId');
  const watchEndPolicy = watch('endPolicy');
  const watchPaymentFrequency = watch('paymentFrequency');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchNumOfOccurrences = watch('numOfOccurrences');

  const { data: selectedVendor } = useVendor({ id: watchVendorId, enabled: false });
  const { track } = useAnalytics();

  useEffect(() => {
    onSelectedVendorChange?.(watchVendorId);
  }, [watchVendorId, onSelectedVendorChange]);

  useEffect(() => {
    const isRecurringSupportedForSelectedVendor =
      isRecurringPaymentsEnabled &&
      (isInternationalSupportedForRecurringFlow || selectedVendor?.businessGeography !== 'international');
    if (!isRecurringSupportedForSelectedVendor) {
      setValue('paymentFrequency', RECURRING_SINGLE_PAYMENT);
    }
    setIsRecurringSupported(isRecurringSupportedForSelectedVendor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternationalSupportedForRecurringFlow, isRecurringPaymentsEnabled, selectedVendor]);

  useEffect(() => {
    onUpdateFrequency?.(watchPaymentFrequency !== RECURRING_SINGLE_PAYMENT);
  }, [watchPaymentFrequency, onUpdateFrequency]);

  const updateValidations = useCallback(() => {
    if (watchPaymentFrequency !== RECURRING_SINGLE_PAYMENT) {
      if (watchEndPolicy === BillSubscriptionEndPolicyEnum.NumOfOccurrences) {
        setFieldsForValidation(recurringNumOfOccurrencesFields);
      } else {
        setFieldsForValidation(recurringEndDateFields);
      }
    } else {
      setFieldsForValidation(singlePaymentFields);
    }
  }, [watchPaymentFrequency, watchEndPolicy]);

  useEffect(() => {
    if (!isEmpty(formState.errors)) {
      const errorType = keys(formState.errors)?.[0];

      if (errorType) {
        track('Bill', 'Status', {
          PageName: 'bill-details',
          Flow: 'simplified',
          Intent: 'add-bill',
          Status: 'failure',
          ErrorType: `${errorType}-error`,
        });
      }
    }
  }, [formState.errors, track]);

  useEffect(() => {
    updateValidations();
  }, [watchPaymentFrequency, watchEndPolicy, updateValidations, watchVendorId]);

  const { onFieldKeyDown: onVendorSelectKeyDown, onFieldFocus: onVendorSelectFocus } = useTrackFirstKeyPress(
    'FillVendorDetails',
    'Submitted'
  );

  const vendorInputProps = {
    colSpan: 2,
    labelProps: { label: formatMessage('activities.addInstantBillV2Form.vendorName.label') },
    placeholder: formatMessage('activities.addInstantBillV2Form.vendorName.placeholder'),
    onKeyDown: onVendorSelectKeyDown,
    onFocus: onVendorSelectFocus,
  };

  const startDateHelperText = getRecurringStartDateHelperText(
    watchPaymentFrequency as BillSubscriptionIntervalTypeEnum,
    watchStartDate as Date,
    formatMessage
  );
  const endDateHelperText = () => {
    const calculatedEndDate = calculateRecurringEffectiveEndDate(watchPaymentFrequency, watchStartDate, watchEndDate);
    return calculatedEndDate
      ? formatMessage('activities.addInstantBillV2Form.numOfOccurrences.helperText', {
          date: formatDate(calculatedEndDate, { dateStyle: 'medium' }),
        })
      : '';
  };

  const numOfOccurrencesHelperText = () => {
    const calculatedEndDate = watchStartDate
      ? calculateRecurringEndDateByNumOfOccurrences({
          paymentFrequency: watchPaymentFrequency as BillSubscriptionIntervalTypeEnum,
          startDate: watchStartDate,
          numOfOccurrences: watchNumOfOccurrences,
        })
      : null;

    return calculatedEndDate
      ? formatMessage('activities.addInstantBillV2Form.numOfOccurrences.helperText', {
          date: formatDate(calculatedEndDate, { dateStyle: 'medium' }),
        })
      : '';
  };

  const endPolicyOptions: Array<{
    label: string;
    value: BillSubscriptionEndPolicyEnum;
    testId: BillSubscriptionEndPolicyEnum;
  }> = [
    {
      label: formatMessage('activities.addInstantBillV2Form.endPolicy.noEndDate.value'),
      value: BillSubscriptionEndPolicyEnum.NoEndDate,
      testId: BillSubscriptionEndPolicyEnum.NoEndDate,
    },
    {
      label: 'Date',
      value: BillSubscriptionEndPolicyEnum.EndDate,
      testId: BillSubscriptionEndPolicyEnum.EndDate,
    },
    {
      label: 'Occurrences',
      value: BillSubscriptionEndPolicyEnum.NumOfOccurrences,
      testId: BillSubscriptionEndPolicyEnum.NumOfOccurrences,
    },
  ];

  return (
    <Form size="small" {...formProps} columns={2} {...props}>
      <VendorSelectWidget
        {...registerField('vendorId')}
        {...vendorInputProps}
        placeholder={formatMessage('activities.addInstantBillV2Form.vendorPicker.placeholder')}
      />

      <Form.AmountField
        {...registerField('billAmount')}
        colSpan={isRecurringSupported ? 1 : 2}
        labelProps={{ label: formatMessage('activities.addInstantBillV2Form.billAmount.label') }}
      />

      <Form.DeprecatedSelect
        colSpan={1}
        labelProps={{ label: formatMessage('activities.addInstantBillV2Form.paymentFrequency.label') }}
        {...registerField('paymentFrequency')}
        options={[
          { label: RECURRING_SINGLE_PAYMENT, value: RECURRING_SINGLE_PAYMENT },
          {
            label: formatMessage('activities.addInstantBillV2Form.paymentFrequency.weekly'),
            value: BillSubscriptionIntervalTypeEnum.Weekly,
            // testId: BillSubscriptionIntervalTypeEnum.Weekly,  // restore once migrated to Form.Select
          },
          {
            label: formatMessage('activities.addInstantBillV2Form.paymentFrequency.monthly'),
            value: BillSubscriptionIntervalTypeEnum.Monthly,
            // testId: BillSubscriptionIntervalTypeEnum.Monthly, // restore once migrated to Form.Select
          },
        ]}
        isHidden={!isRecurringSupported}
        isRequired
        data-testid="add-instant-v2-frequency"
        isClearable={false} // replace with emptyState={undefined} once migrated to Form.Select
        shouldHideCaret
      />
      <Form.DateField
        colSpan={1}
        isHidden={watchPaymentFrequency === RECURRING_SINGLE_PAYMENT}
        labelProps={{ label: formatMessage('activities.addInstantBillV2Form.startDate.label') }}
        placeholder={formatMessage('activities.addInstantBillV2Form.startDate.placeholder')}
        minDate={createDate()}
        helperTextProps={{ label: startDateHelperText }}
        legendItems={
          watchStartDate && [
            {
              label: formatMessage('activities.addInstantBillV2Form.startDate.legendItems.arrivalDate'),
            },
          ]
        }
        excludeHolidays
        {...registerField('startDate')}
      />
      <Form.DeprecatedSelect
        labelProps={{ label: formatMessage('activities.addInstantBillV2Form.endPolicy.label') }}
        {...registerField('endPolicy')}
        isHidden={watchPaymentFrequency === RECURRING_SINGLE_PAYMENT}
        options={endPolicyOptions}
        isRequired
        data-testid="add-instant-v2-policy"
        isClearable={false} // replace with emptyState={undefined} once migrated to Form.Select
        shouldHideCaret
      />
      <Form.DateField
        labelProps={{ label: formatMessage('activities.addInstantBillV2Form.endDate.label') }}
        isHidden={
          watchPaymentFrequency === RECURRING_SINGLE_PAYMENT || watchEndPolicy !== BillSubscriptionEndPolicyEnum.EndDate
        }
        placeholder={formatMessage('activities.addInstantBillV2Form.endDate.placeholder')}
        minDate={watchStartDate || createDate()}
        helperTextProps={{ label: endDateHelperText() }}
        excludeHolidays
        {...registerField('endDate')}
        legendItems={
          watchEndDate && [
            {
              label: formatMessage('activities.addInstantBillV2Form.endDate.legendItems.endDate'),
            },
          ]
        }
        isRequired
      />
      <Form.TextField
        labelProps={{ label: formatMessage('activities.addInstantBillV2Form.numOfOccurrences.label') }}
        isHidden={
          watchPaymentFrequency === RECURRING_SINGLE_PAYMENT ||
          watchEndPolicy !== BillSubscriptionEndPolicyEnum.NumOfOccurrences
        }
        placeholder={formatMessage('activities.addInstantBillV2Form.numOfOccurrences.placeholder')}
        helperTextProps={{ label: numOfOccurrencesHelperText() || '' }}
        maskProps={{ mask: RECURRING_NUM_OF_OCCURRENCES_REGEX }}
        {...registerField('numOfOccurrences')}
        data-testid="add-instant-v2-occurrences"
        isRequired
      />

      {watchPaymentFrequency !== RECURRING_SINGLE_PAYMENT && (
        <Form.ContentBox colSpan={2}>
          <RecurringInfoSection />
        </Form.ContentBox>
      )}

      {!!activeAccountingPlatform && (
        <AccountingPlatformCategorySelectWidget
          {...registerField('categoryId')}
          activeAccountingPlatform={activeAccountingPlatform}
          data-testid="add-instant-v2-category"
          colSpan={2}
          labelProps={{
            label: accountingPlatformCategoryLabel,
          }}
        />
      )}
    </Form>
  );
};
